import { useEffect, useState, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTokenExpiration } from '../../hooks/useTokenExpiration'
import { paths } from '../../router/paths'
import type { RootState } from '../../redux/global-store'
import { logOut } from '../../features/authentication/presentation/redux/actions/log-out-action'
import { RefreshJwtRequest, RefreshJwtReset } from '../../features/authentication/presentation/redux/actions/refresh-jwt-actions'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { Clock } from '../icons/clock'

export const SessionExpirationModal = () => {
  const [open, setOpen] = useState(false)
  const [userDismissed, setUserDismissed] = useState(false)
  const [dragStartY, setDragStartY] = useState(0)
  const [dragCurrentY, setDragCurrentY] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  
  const token = localStorage.getItem('token')
  const { user } = useSelector((state: RootState) => state.auth.login)
  const { success, error, load } = useSelector((state: RootState) => state.auth.refreshJwt)
  
  const { isExpiring, isExpired, timeLeft } = useTokenExpiration(token)

  const handleLogout = useCallback(() => {
    setOpen(false)
    localStorage.removeItem('token')
    sessionStorage.removeItem('logged')
    
    // Usar setTimeout para evitar conflitos de renderização concorrente
    setTimeout(() => {
      dispatch(logOut())
      navigate(paths.authentication.login, { replace: true })
    }, 0)
  }, [dispatch, navigate])

  useEffect(() => {
    if (isExpiring && !open && !userDismissed) {
      // Resetar estado antes de abrir a modal
      dispatch(RefreshJwtReset())
      setOpen(true)
    }
  }, [isExpiring, open, userDismissed, dispatch])

  // Logout automático quando token expirar completamente
  useEffect(() => {
    if (isExpired) {
      toast.error('Su sesión ha expirado')
      handleLogout()
    }
  }, [isExpired, handleLogout])

  // Monitorar sucesso do refresh
  useEffect(() => {
    if (success && open) {
      setOpen(false)
      setUserDismissed(false) // Resetar para permitir que o modal apareça novamente se necessário
      toast.success('Sesión renovada exitosamente')
      
      // Resetar o estado após 1 segundo para evitar re-execução
      setTimeout(() => {
        dispatch(RefreshJwtReset())
      }, 1000)
    }
  }, [success, open, dispatch])

  // Monitorar erro do refresh
  useEffect(() => {
    if (error && open) {
      toast.error(`Error al renovar la sesión: ${error}`)
      handleLogout()
    }
  }, [error, open, handleLogout])

  const handleRefreshToken = () => {
    if (!user?.userId || !user?.taxId) {
      toast.error('Error: datos de usuario no disponibles')
      handleLogout()
      return
    }

    dispatch(
      RefreshJwtRequest({
        login: user.taxId,
        userId: user.userId,
        companyId: 0,
      })
    )
  }


  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const handleDragStart = (clientY: number) => {
    setDragStartY(clientY)
    setDragCurrentY(clientY)
    setIsDragging(true)
  }

  const handleDragMove = (clientY: number) => {
    if (isDragging) {
      const diff = clientY - dragStartY
      if (diff > 0) { // Só permite arrastar para baixo
        setDragCurrentY(clientY)
      }
    }
  }

  const handleDragEnd = () => {
    if (isDragging) {
      const diff = dragCurrentY - dragStartY
      if (diff > 80) { // Se arrastar mais de 80px, fecha
        setOpen(false)
        setUserDismissed(true)
      }
      setIsDragging(false)
      setDragStartY(0)
      setDragCurrentY(0)
    }
  }

  const translateY = isDragging ? Math.max(0, dragCurrentY - dragStartY) : 0

  // Adicionar listeners globais para mouse
  useEffect(() => {
    if (!isDragging) return

    const handleGlobalMouseMove = (e: MouseEvent) => {
      e.preventDefault()
      handleDragMove(e.clientY)
    }

    const handleGlobalMouseUp = () => {
      handleDragEnd()
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    document.addEventListener('mouseup', handleGlobalMouseUp)

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
      document.removeEventListener('mouseup', handleGlobalMouseUp)
    }
  }, [isDragging, dragStartY, dragCurrentY])

  return (
    <Dialog
      open={open}
      onClose={() => {}} // Não permitir fechar clicando fora
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          margin: 0,
          width: '100%',
          maxWidth: '100%',
          borderRadius: '16px 16px 0 0',
          padding: 2,
          transform: `translateY(${translateY}px)`,
          transition: isDragging ? 'none' : 'transform 0.3s ease-out',
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-end',
        },
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
        }
      }}
    >
      <Box
        sx={{
          width: '100%',
          paddingTop: 1,
          paddingBottom: 1,
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
          userSelect: 'none',
        }}
        onMouseDown={(e) => {
          e.preventDefault()
          handleDragStart(e.clientY)
        }}
        onTouchStart={(e) => {
          e.preventDefault()
          handleDragStart(e.touches[0].clientY)
        }}
        onTouchMove={(e) => {
          e.preventDefault()
          handleDragMove(e.touches[0].clientY)
        }}
        onTouchEnd={(e) => {
          e.preventDefault()
          handleDragEnd()
        }}
      >
        <Box 
          sx={{ 
            width: '40px',
            height: '4px',
            backgroundColor: '#D1D5DB',
            borderRadius: '2px',
            margin: '0 auto',
          }}
        />
      </Box>
      <Box position="absolute" top={8} right={8}>
        <IconButton
          onClick={() => {
            setOpen(false)
            setUserDismissed(true)
          }}
          size="small"
          sx={{
            color: '#666',
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            }
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent sx={{ textAlign: 'center', paddingTop: 3, paddingBottom: 2 }}>
        <Box display="flex" justifyContent="center" mb={2}>
          <Clock style={{ width: 111, height: 105 }} />
        </Box>
        <Typography variant="h6" component="div" fontWeight="600" gutterBottom>
          Ops! Su sesión está a punto de expirar.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          ¡Haga clic en el botón a continuación para renovar su sesión en hasta <strong>{formatTime(timeLeft)}</strong> y continuar navegando sin interrupciones!
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ padding: 2, paddingTop: 0 }}>
        <Button
          onClick={handleRefreshToken}
          variant="contained"
          fullWidth
          disabled={load}
          sx={{ 
            backgroundColor: '#1a1a1a',
            color: 'white',
            textTransform: 'none',
            fontSize: '16px',
            fontWeight: '500',
            padding: '12px',
            borderRadius: 2,
            '&:hover': {
              backgroundColor: '#2a2a2a',
            },
            '&:disabled': {
              backgroundColor: '#cccccc',
            }
          }}
        >
          {load ? 'Renovando...' : 'Mantente conectado'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
